import type { SupportTicketStatusFields, SupportTicketSummary } from 'chaika-frontend-types/frontend-cms';

import type { SupportTicketListQuery } from '@api/SupportApi';
import { translate } from '@chaika/i18n/helper';

export type SupportStatusFilter = { kind: 'open' } | { kind: 'all' } | { kind: 'names'; names: string[] };

export interface SupportTicketListFilter {
    status: SupportStatusFilter;
    q: string;
}

export const DEFAULT_TICKET_LIST_FILTER: SupportTicketListFilter = { status: { kind: 'open' }, q: '' };

const SEARCH_LOCALE = 'ru';
const TICKET_NUMBER_QUERY_PATTERN = /^[#№]?\s*(\d{1,9})$/u;

export function isOpenTicket(ticket: SupportTicketStatusFields): boolean {
    return ticket.status !== 'deleted' && ticket.statusGroup !== 'complete';
}

export function matchesTicketListFilter(ticket: SupportTicketSummary, filter: SupportTicketListFilter): boolean {
    if (!matchesSearchQuery(ticket, filter.q)) {
        return false;
    }

    switch (filter.status.kind) {
        case 'all':
            return true;
        case 'open':
            return isOpenTicket(ticket);
        case 'names':
            return ticket.status !== 'deleted' && filter.status.names.includes(ticket.statusName);
    }
}

export function toTicketListQuery(filter: SupportTicketListFilter, cursor?: string): SupportTicketListQuery {
    const query = filter.q.trim();

    return {
        ...toStatusQuery(filter.status),
        ...(query ? { q: query } : {}),
        ...(cursor ? { cursor } : {}),
    };
}

export function toggleStatusName(
    filter: SupportStatusFilter,
    name: string,
    allStatusNames: string[],
): SupportStatusFilter {
    const selectedNames = filter.kind === 'names' ? filter.names : [];
    const names = selectedNames.includes(name)
        ? selectedNames.filter(selectedName => selectedName !== name)
        : [...selectedNames, name];

    if (names.length === 0) {
        return { kind: 'open' };
    }
    if (allStatusNames.every(statusName => names.includes(statusName))) {
        return { kind: 'all' };
    }

    return { kind: 'names', names };
}

export function describeStatusFilter(filter: SupportStatusFilter): string {
    switch (filter.kind) {
        case 'open':
            return translate('supportFilterOpenOnly');
        case 'all':
            return translate('supportFilterAllStatuses');
        case 'names':
            return filter.names.length === 1
                ? filter.names[0]
                : translate('supportFilterStatusesCount', { $count: String(filter.names.length) });
    }
}

function matchesSearchQuery(ticket: SupportTicketSummary, rawQuery: string): boolean {
    const query = rawQuery.trim();
    if (query === '') {
        return true;
    }
    if (ticket.title.toLocaleLowerCase(SEARCH_LOCALE).includes(query.toLocaleLowerCase(SEARCH_LOCALE))) {
        return true;
    }
    const queriedNumber = TICKET_NUMBER_QUERY_PATTERN.exec(query)?.[1];

    return queriedNumber !== undefined && ticket.number === Number(queriedNumber);
}

function toStatusQuery(filter: SupportStatusFilter): Pick<SupportTicketListQuery, 'group' | 'status'> {
    switch (filter.kind) {
        case 'open':
            return { group: ['to_do', 'in_progress'] };
        case 'all':
            return {};
        case 'names':
            return { status: filter.names };
    }
}
